// src/lib/menage-log-info-cache.js — Phase 4 PR #141 (ADR-077)
// + PR #148 BUG 1 : TTL 60s + invalidation visibilitychange.
// + PR #151 PARTIE F (ADR-085) : cache key composé `${id}:${date}`
//   pour supporter `scheduled_date` côté payload (timing).
//
// Cache module-level pour les infos logement côté ménage. RLS storage
// tightened (PR #140) bloque la lecture client direct de fbz8-cfg pour
// le rôle menage → on passe par /api/admin-users?action=menage_log_info.
//
// PR #148 — fix BUG 1 (cache stale) : avant, "pas de TTL" car session
// ménage courte et codes/checklists changent rarement. Mais en pratique,
// quand l'admin modifie une checklist dans un autre onglet pendant que
// le ménage a sa modale ouverte, le cache restait stale jusqu'à un
// hard-refresh. Mitigations en cascade :
//   1) TTL 60s → la 1ère ouverture après modif voit toujours les données
//      fraîches sous 60s, sans intervention utilisateur.
//   2) Listener visibilitychange → quand l'onglet redevient visible,
//      cache.clear() force un refetch au prochain fetchLogInfo.
//   3) Bouton "↻ Recharger" dans ChecklistRunModal (cf. invalidateLogInfo).
//
// PR #151 — Cache key composé : avant `cache.set(logementId)`, mais le
// payload dépend désormais de `scheduled_date` (timing checkout/checkin).
// On compose `${logementId}:${scheduledDate || 'no-date'}`. invalidateLogInfo(id)
// purge TOUTES les keys préfixées `${id}:*` (pas juste no-date).
//
// Cache d'erreur reste sans TTL (cf. cache.delete dans le catch) :
// si fetch erreur, on n'entrée rien en cache, donc le prochain appel
// retentera systématiquement.

import { supabase } from "../supabase"

const TTL_MS = 60_000 // 60 sec — fenêtre max de "stale" tolérée
const cache = new Map() // `${id}:${date|no-date}` → { promise } | { data, ts }

function buildCacheKey(logementId, scheduledDate) {
  return `${logementId}:${scheduledDate || 'no-date'}`
}

export async function fetchLogInfo(logementId, scheduledDate) {
  if (!logementId) throw new Error('logement_id requis')

  const cacheKey = buildCacheKey(logementId, scheduledDate)

  // Check cache (data + TTL non expiré)
  const cached = cache.get(cacheKey)
  if (cached?.data && typeof cached.ts === 'number' && (Date.now() - cached.ts) < TTL_MS) {
    return cached.data
  }
  if (cached?.promise) return cached.promise
  // Si TTL expiré, on purge l'entrée stale et on refetch
  if (cached?.data) cache.delete(cacheKey)

  // Fetch
  const promise = (async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) throw new Error('Session expirée')
    let url = `/api/admin-users?action=menage_log_info&logement_id=${encodeURIComponent(logementId)}`
    if (scheduledDate) {
      url += `&scheduled_date=${encodeURIComponent(scheduledDate)}`
    }
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    const data = await res.json()
    cache.set(cacheKey, { data, ts: Date.now() })
    return data
  })()
  cache.set(cacheKey, { promise })
  try {
    const data = await promise
    return data
  } catch (e) {
    cache.delete(cacheKey) // permettre retry
    throw e
  }
}

// Pour invalidation manuelle si besoin (admin a modifié codes côté
// admin pendant que le ménage est sur place — cas rare, désormais
// exposé via bouton "↻ Recharger" dans ChecklistRunModal).
//
// PR #151 PARTIE F : si logementId fourni, purge TOUTES les keys
// préfixées `${logementId}:*` (peut y avoir plusieurs entrées avec
// dates différentes pour le même logement).
export function invalidateLogInfo(logementId) {
  if (logementId) {
    const prefix = `${logementId}:`
    for (const key of cache.keys()) {
      if (key.startsWith(prefix)) cache.delete(key)
    }
  } else {
    cache.clear()
  }
}

// PR #148 BUG 1 — invalidation cross-tab : quand l'onglet ménage
// redevient visible (l'admin a probablement modifié la config dans un
// autre onglet entre temps), on purge tout le cache. Combiné avec le
// TTL 60s, garantit que les changements admin sont visibles côté
// ménage en max 60s ou au focus.
//
// Le listener vit toute la session (singleton ESM, pas de cleanup
// nécessaire). Garde `typeof document !== 'undefined'` pour le SSR /
// environnements de test où document peut être absent.
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      cache.clear()
    }
  })
}

// L-1 (2026-08-19) — L'APPEL GROUPÉ : un seul aller-retour pour N logements
// d'une même date (la frise appelait le guichet une fois PAR logement).
// Chaque payload PRIME le cache unitaire (même clé, même TTL) : fetchLogInfo
// derrière (menus, panneau, modale checklist) devient un hit immédiat.
// Un logement en erreur (hors périmètre, introuvable) est ABSENT du retour —
// jamais un échec global pour un lid fautif.
export async function fetchLogInfos(logementIds, scheduledDate) {
  const lids = [...new Set((logementIds || []).filter(Boolean))]
  if (!lids.length) return {}
  const resultat = {}
  const manquants = []
  for (const lid of lids) {
    const cached = cache.get(buildCacheKey(lid, scheduledDate))
    if (cached?.data && typeof cached.ts === 'number' && (Date.now() - cached.ts) < TTL_MS) {
      resultat[lid] = cached.data
    } else {
      manquants.push(lid)
    }
  }
  if (!manquants.length) return resultat

  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  let url = `/api/admin-users?action=menage_log_info&logement_ids=${encodeURIComponent(manquants.join(','))}`
  if (scheduledDate) url += `&scheduled_date=${encodeURIComponent(scheduledDate)}`
  const res = await fetch(url, { headers: { Authorization: `Bearer ${session.access_token}` } })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  const json = await res.json()
  const infos = json?.infos && typeof json.infos === 'object' ? json.infos : {}
  for (const [lid, data] of Object.entries(infos)) {
    cache.set(buildCacheKey(lid, scheduledDate), { data, ts: Date.now() })
    resultat[lid] = data
  }
  for (const [lid, message] of Object.entries(json?.erreurs || {})) {
    console.error('[log-info] groupé', lid, message)
  }
  return resultat
}

